/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import { EuiLink, EuiSkeletonText, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import { addToast } from '@modules/cui/Toasts'
import Breadcrumbs from '@modules/cui/Breadcrumbs/Breadcrumbs'
import { projectCpsWizardCrumbs } from '@modules/project-user-pages/breadcrumbs'
import { projectCpsUrl } from '@modules/project-user-lib/urls'
import type { AnyProject } from '@modules/project-api/types'
import type { ProjectID, ProjectType } from '@modules/ui-types/projects'
import ProjectApiErrorCallout from '@modules/project-components/ProjectApiErrorCallout'
import { MAX_LINKED_PROJECTS } from '@modules/project-components/ProjectCps/ProjectCpsPage'
import { useProjectCpsContext } from '@modules/project-components/ProjectCps/ProjectCpsContext'
import { useIsCpsFeatureActive } from '@modules/project-components/ProjectCps/hooks'

import ProjectCpsLinkContainer from './ProjectCpsLinkContainer'
import { useLinkProjects } from './hooks/useLinkProjects'
import { useEbtProjectCpsWizard } from './hooks/useEbtProjectCpsWizard'
import CancelConfigurationModal from './CancelConfigurationModal'
import SelectProjectsStepContainer from './stepOne/SelectProjectsStepContainer'
import ReviewAndSaveStepContainer from './stepTwo/ReviewAndSaveStepContainer'

type Props = {
  projectType: ProjectType
}

const ProjectCpsLinkPage: React.FunctionComponent<Props> = ({ projectType }) => {
  const { projectId } = useParams() as { projectId: ProjectID }
  const navigate = useNavigate()
  const { useGetProjectQuery } = useProjectCpsContext()
  const isCpsEnabled = useIsCpsFeatureActive()

  const { data: project, error: projectError } = useGetProjectQuery(projectType, projectId) ?? {}
  const { linkProjects, resetErrors, isLoading: isSaving, error: linkError } = useLinkProjects()
  const {
    reportProjectCpsWizardNextStepEbt,
    reportProjectCpsWizardPreviousStepEbt,
    reportProjectCpsWizardCancelEbt,
    reportProjectCpsWizardSubmitEbt,
  } = useEbtProjectCpsWizard()

  const [currentStep, setCurrentStep] = useState(1)
  const [selectedProjects, setSelectedProjects] = useState<AnyProject[]>([])
  const [isCancelModalVisible, setIsCancelModalVisible] = useState(false)

  if (!isCpsEnabled) {
    return <ProjectApiErrorCallout error='Cross-project search feature is not enabled' />
  }

  if (projectError) {
    return <ProjectApiErrorCallout error={projectError} />
  }

  if (!project) {
    return <EuiSkeletonText />
  }

  const linkedProjectIds = Object.keys(project.linked?.projects || {})
  const isEditing = linkedProjectIds.length > 0
  const hasSelection = selectedProjects.length > 0
  const totalLinkedCount = linkedProjectIds.length + selectedProjects.length
  const hasReachedMaxLinkedProjects = totalLinkedCount > MAX_LINKED_PROJECTS

  const handleCancel = () => {
    if (currentStep === 2) {
      // If on step 2, show confirmation modal
      setIsCancelModalVisible(true)
    } else if (currentStep === 1 && hasSelection) {
      // If on step 1 and has selection, show confirmation modal
      setIsCancelModalVisible(true)
    } else {
      // Otherwise, navigate away directly
      reportProjectCpsWizardCancelEbt({
        project_id: projectId,
        project_type: projectType,
        current_step: currentStep,
        selected_projects_count: selectedProjects.length,
        is_editing: isEditing,
      })
      navigate(projectCpsUrl(projectType, projectId))
    }
  }

  const handleConfirmCancel = () => {
    reportProjectCpsWizardCancelEbt({
      project_id: projectId,
      project_type: projectType,
      current_step: currentStep,
      selected_projects_count: selectedProjects.length,
      is_editing: isEditing,
    })
    setIsCancelModalVisible(false)
    navigate(projectCpsUrl(projectType, projectId))
  }

  const handleCloseCancelModal = () => {
    setIsCancelModalVisible(false)
  }

  const handleReviewAndSave = () => {
    reportProjectCpsWizardNextStepEbt({
      project_id: projectId,
      project_type: projectType,
      current_step: currentStep,
      selected_projects_count: selectedProjects.length,
      is_editing: isEditing,
    })
    setCurrentStep(2)
  }

  const handlePrevious = () => {
    reportProjectCpsWizardPreviousStepEbt({
      project_id: projectId,
      project_type: projectType,
      current_step: currentStep,
      selected_projects_count: selectedProjects.length,
      is_editing: isEditing,
    })
    resetErrors()
    setCurrentStep(1)
  }

  const handleSave = async () => {
    try {
      await linkProjects({
        projectId,
        projectType,
        selectedProjects,
      })

      reportProjectCpsWizardSubmitEbt({
        project_id: projectId,
        project_type: projectType,
        current_step: currentStep,
        selected_projects_count: selectedProjects.length,
        is_editing: isEditing,
      })

      addToast({
        title: (
          <FormattedMessage id='cps.link.success.title' defaultMessage='Configuration saved' />
        ),
        color: 'success',
        text: (
          <FormattedMessage
            id='cps.link.success.text'
            defaultMessage="You'll be able to access data from your linked {count, plural, one {project} other {projects}} shortly."
            values={{ count: selectedProjects.length }}
          />
        ),
      })

      // Navigate back to CPS page on success
      navigate(projectCpsUrl(projectType, projectId))
    } catch (error) {
      // Error is displayed via linkError in ReviewAndSaveStepContainer
      // Stay on Step 2 so user can see the error and retry
    }
  }

  const renderStepContent = () => {
    if (currentStep === 1) {
      return (
        <SelectProjectsStepContainer
          projectId={projectId}
          projectType={projectType}
          linkedProjectIds={linkedProjectIds}
          selectedProjects={selectedProjects}
          onSelectionChange={setSelectedProjects}
          hasSelection={hasSelection}
          hasReachedMaxLinkedProjects={hasReachedMaxLinkedProjects}
          onCancel={handleCancel}
          onReviewAndSave={handleReviewAndSave}
        />
      )
    }

    return (
      <ReviewAndSaveStepContainer
        projectId={projectId}
        projectType={projectType}
        selectedProjects={selectedProjects}
        projectName={project.name}
        isEditing={isEditing}
        linkError={linkError}
        isSaving={isSaving}
        onCancel={handleCancel}
        onPrevious={handlePrevious}
        onSave={handleSave}
      />
    )
  }

  const pageTitle = (
    <EuiText textAlign='center'>
      <h1>
        <FormattedMessage id='cps.link.title' defaultMessage='Link projects' />
      </h1>
      <p>
        <FormattedMessage
          id='cps.link.description'
          defaultMessage='Search and analyze data across multiple projects from the {projectName} project. {learnMoreLink}'
          values={{
            projectName: (
              <EuiText color='accent' component='span'>
                {project.name}
              </EuiText>
            ),
            learnMoreLink: (
              // TODO: https://github.com/elastic/cloud-ui/issues/952
              <EuiLink
                external={true}
                href='https://www.elastic.co/TBD'
                target='_target'
                data-test-subj='cps-linkPage-learnMoreLink'
              >
                <FormattedMessage id='cps.link.learnMore' defaultMessage='Learn more' />
              </EuiLink>
            ),
          }}
        />
      </p>
    </EuiText>
  )

  return (
    <Fragment>
      <Breadcrumbs
        breadcrumbs={projectCpsWizardCrumbs({
          projectType,
          projectId,
          projectName: project.name,
        })}
      />

      <ProjectCpsLinkContainer
        step={currentStep}
        title={pageTitle}
        setCurrentStep={setCurrentStep}
        resetErrors={resetErrors}
      >
        {renderStepContent()}
      </ProjectCpsLinkContainer>

      {isCancelModalVisible && (
        <CancelConfigurationModal
          onCancel={handleCloseCancelModal}
          onConfirm={handleConfirmCancel}
        />
      )}
    </Fragment>
  )
}

export default ProjectCpsLinkPage
